import React, { useState } from 'react';
import '../styles/LeadCaptureModal.css';
import { User, Mail, Phone, Lock, Send } from 'lucide-react';
import { getApiBaseUrl } from '../config';

function LeadCaptureModal({ scholarships, profile, onClose, onSuccess }) {
  const apiUrl = getApiBaseUrl();
  const scholarshipCount = scholarships?.scholarships?.length || scholarships?.length || 0;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      setError('Please fill all fields');
      return;
    }

    const sanitizedPhone = formData.phone.trim();
    const digitCount = sanitizedPhone.replace(/\D/g, '').length;
    if (digitCount < 7) {
      setError('Please enter a valid WhatsApp number.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        website: formData.website,
        user_profile: profile,
        scholarship_results: scholarships
      };

      const response = await fetch(`${apiUrl}/api/submit-lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit form');
      }

      const result = await response.json();
      if (!result.email_sent) {
        setError(result.message || 'We saved your details, but could not send the email right now.');
        return;
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err.message || 'Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-capture-title"
      >
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close lead form">✕</button>
        
        <div className="modal-header">
          <div className="unlock-icon">
            <Lock size={56} color="var(--primary-light)" />
          </div>
          <h2 id="lead-capture-title">Unlock Your Full List</h2>
          <p className="unlock-subtitle">+ AI Essay Strategy & Personalized Tips</p>
        </div>

        <p className="unlock-description">
          Get instant access to all {scholarshipCount} scholarships, winning strategies for each, and a personalized action plan.
        </p>

        {error && (
          <div className="error-banner">
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="lead-form">
          <div className="form-group">
            <label htmlFor="name"><User size={18} /> Full Name *</label>
            <input
              id="name"
              name="name"
              type="text"
              placeholder="Your full name"
              value={formData.name}
              onChange={handleInputChange}
              className="form-input"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email"><Mail size={18} /> Email Address *</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={handleInputChange}
              className="form-input"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone"><Phone size={18} /> WhatsApp Number *</label>
            <input
              id="phone"
              name="phone"
              type="tel"
              placeholder="+91 9876543210"
              value={formData.phone}
              onChange={handleInputChange}
              className="form-input"
              required
              disabled={loading}
            />
          </div>

          <input
            name="website"
            type="text"
            value={formData.website}
            onChange={handleInputChange}
            tabIndex="-1"
            autoComplete="off"
            aria-hidden="true"
            className="visually-hidden-honeypot"
          />

          <button 
            type="submit" 
            className="btn-submit"
            disabled={loading}
          >
            {loading ? 'Sending your report...' : <><Send size={20} /> Send My Full Report</>}
          </button>

          <p className="form-footer">
            ✅ We'll email your full report instantly<br/>
            📧 Check your inbox (and spam folder) within 2 minutes
          </p>
        </form>

        <p className="modal-footer">
          Takes less than 60 seconds ⚡
        </p>
      </div>
    </div>
  );
}

export default LeadCaptureModal;
